import { Checkbox, Chip, Typography, type SxProps } from '@mui/material';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { FormikCustomMultiAutocompleteField } from '@components/FormikFields';
import Icon from '@components/Icon/Icon';

import useReferenceRange from '@hooks/useReferenceRange';
import { useSession } from '@hooks/useSession';

import type { RefRangeOption } from '@models/alert';

import { capitalize } from '@utils/format';
import {
  displayFriendlyRiskLevel,
  getReferenceRangeMap,
} from '@utils/reference_range';

import { useGetReferenceRangesLazyQuery } from 'generated/reference_range.generated';
import type {
  ReferenceRangeRiskLevel,
  ReferenceRangeRiskModel,
} from 'generated/types';

interface ReferenceRangeAutocompleteProps {
  category: string;
  code: string;
  label?: string;
  name: string;
  required?: boolean;
  limitChips?: number;
}

export default function ReferenceRangeAutocomplete({
  category,
  code,
  label,
  name,
  required,
}: ReferenceRangeAutocompleteProps) {
  const [open, setOpen] = useState<boolean>(false);
  const [referenceRange, setReferenceRange] = useState<
    Map<ReferenceRangeRiskLevel, ReferenceRangeRiskModel>
  >(new Map());

  const { formatMessage } = useIntl();
  const {
    state: { activeProfile },
  } = useSession();
  const { displayFriendlyRange, parameterMap } = useReferenceRange();

  const [getReferenceRanges, { loading }] = useGetReferenceRangesLazyQuery();

  const handleOpen = () => {
    const vitall_patient_id = activeProfile?.vitall_patient_id;
    setOpen(true);
    if (!code || !vitall_patient_id) {
      return;
    }
    (async () => {
      const { data: refRangesData } = await getReferenceRanges({
        variables: {
          input: {
            category,
            codes: [code],
            vitall_patient_id,
          },
        },
      });
      const refRanges = refRangesData?.getReferenceRanges.at(0);
      const map = refRanges ? getReferenceRangeMap(refRanges) : new Map();
      setReferenceRange(map);
    })();
  };

  const handleClose = () => {
    setOpen(false);
    setReferenceRange(new Map());
  };

  const options = Array.from(referenceRange.entries()).reduce<RefRangeOption[]>(
    (acc, [riskLevel, refRanges]) => {
      for (const param of refRanges.parameters) {
        const { code: range_code, parameter_id, vitall_patient_id } = param;
        acc.push({
          id: parameter_id,
          risk: riskLevel,
          friendly_label: displayFriendlyRange({
            code,
            parameters: [
              {
                code: param.code,
                lower_bound: param.lower_bound,
                parameter_id,
                upper_bound: param.upper_bound,
              },
            ],
            include_clusivity: false,
            is_custom: Boolean(vitall_patient_id),
          }),
          range_code,
        });
      }
      return acc;
    },
    []
  );

  const formatOptionLabel = ({
    friendly_label,
    range_code,
    risk,
  }: RefRangeOption) => {
    const messageId = displayFriendlyRiskLevel(code, risk);
    const friendly_risk = messageId ? formatMessage({ id: messageId }) : '';
    return `${
      range_code ? `${capitalize(range_code)} - ` : ''
    } ${friendly_risk}: ${friendly_label}`;
  };

  const chipSxProps: SxProps = {
    px: 0.5,
    py: 0.5,
    textTransform: 'none',
    height: 'auto',
    '& .MuiChip-label': {
      display: 'block',
      whiteSpace: 'normal',
    },
  };

  return (
    <FormikCustomMultiAutocompleteField
      async={{
        loading,
        onOpen: handleOpen,
        onClose: handleClose,
        open,
      }}
      disableCloseOnSelect
      getOptionLabel={(option) => formatOptionLabel(option)}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      label={label}
      name={name}
      options={options}
      required={required}
      renderOption={(props, option, { selected }) => {
        return (
          <li {...props} key={`ref-range-autocomplete-${option.id}`}>
            <Checkbox
              icon={<Icon name="square" size="xl" type="far" />}
              checkedIcon={<Icon name="square-check" size="xl" type="fas" />}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            <Chip
              label={
                <Typography
                  color="text.primary"
                  sx={{
                    textTransfom: 'capitalize',
                  }}
                  variant="caption"
                >
                  {formatOptionLabel(option)}
                </Typography>
              }
              sx={{
                ...chipSxProps,
                bgcolor: parameterMap[option.risk].bgColour,
                borderColor: parameterMap[option.risk].colour,
              }}
              variant="outlined"
            />
          </li>
        );
      }}
      renderTags={(value: RefRangeOption[], getTagProps) => {
        return (
          <>
            {value.map((selectedOption, optionIndex) => {
              const { id, risk } = selectedOption;
              return (
                <Chip
                  {...getTagProps({ index: optionIndex })}
                  deleteIcon={<Icon name="circle-xmark" size="lg" type="fas" />}
                  key={`selected-ref-range-${id}`}
                  label={
                    <Typography
                      color="text.primary"
                      sx={{
                        textTransfom: 'capitalize',
                      }}
                      variant="caption"
                    >
                      {formatOptionLabel(selectedOption)}
                    </Typography>
                  }
                  sx={{
                    ...chipSxProps,
                    bgcolor: parameterMap[risk].bgColour,
                    borderColor: parameterMap[risk].bgColour,
                    '& .MuiChip-deleteIcon': {
                      color: parameterMap[risk].colour,
                    },
                  }}
                  variant="outlined"
                />
              );
            })}
          </>
        );
      }}
    />
  );
}
